import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, SelectField } from '@components';
import { Loader, Modal, Status } from '@components/general';
import { CONSTANT } from '@const';
import { LegalEntityContext, ModalContext } from '@context';
import { DeviceReadingService } from '@data';
import { DeviceReading, ReadingStatus, WithdrawalReasonType } from '@types';
import {
	convertDateAndTimeToDisplayFormat,
	displayReadingValue,
	displayResourceSourceType,
	hasAssessedTake,
	safeId,
} from '@utils';

import { Enlarge } from '@/componentsQGDS';
import { RequestedDataRenderer } from '../../requestedDataRendererRQuery/RequestedDataRenderer';
import { AssessedTake } from '../assessedTake/AssessedTake';

import styles from './meterReadingDetails.module.scss';

export const MeterReadingDetails = () => {
	// Context
	const { legalEntity } = useContext(LegalEntityContext);
	const {
		selectedDevice: selectedMeter,
		setSelectedDeviceReading,
		selectedDeviceReading: selectedReading,
		setShowReadingModal,
	} = useContext(ModalContext);
	// State
	const [readingPhotoName] = useState<string>('');
	const [showWithdrawal, setShowWithdrawal] = useState<boolean>(false);
	const [showWithdrawalLink, setShowWithdrawalLink] = useState<boolean>(
		selectedReading?.status == ReadingStatus.REQUIRES_REVIEW ||
			selectedReading?.status == ReadingStatus.SUBMITTED ||
			selectedReading?.status == ReadingStatus.VERIFIED,
	);
	const [withdrawalReason, setWithdrawalReason] = useState<WithdrawalReasonType | undefined>(
		undefined,
	);
	const [showError, setShowError] = useState<boolean>(false);
	const [showSuccess, setShowSuccess] = useState<boolean>(false);
	const [showPhotoModal, setShowPhotoModal] = useState<boolean>(false);

	// Data
	const getMeterReadingImage = useQuery({
		queryKey: [CONSTANT.DEVICE_READING_PHOTO_API_URL, selectedReading?.id],
		queryFn: () =>
			DeviceReadingService.getDeviceReadingImage(
				safeId(legalEntity?.id),
				safeId(selectedMeter?.id),
				safeId(selectedReading?.id),
			),
		// enabled: !!selectedReading?.photoFileName,	// TODO:  uncomment once API implements this field on response data to avoid unnecesary loading when there is no photo
	});

	const updateReading = useMutation({
		mutationFn: (partialReading: Partial<DeviceReading>) =>
			DeviceReadingService.updateDeviceReading(
				safeId(legalEntity?.id),
				safeId(selectedMeter?.id),
				partialReading,
			),
	});

	// Helpers
	const submitWithdrawalForm = async () => {
		const readingUpdates: Partial<DeviceReading> = {
			id: selectedReading?.id,
			withdrawalReason: withdrawalReason,
		};

		await updateReading
			.mutateAsync(readingUpdates)
			.then(() => {
				setSelectedDeviceReading({
					...(selectedReading as DeviceReading),
					status: ReadingStatus.WITHDRAWN,
					withdrawalReason: withdrawalReason,
				});
				setShowSuccess(true);
				setShowError(false);
			})
			.catch(() => {
				setShowError(true);
				setShowSuccess(false);
			})
			.finally(() => {
				setShowWithdrawal(false);
			});
	};

	const handleWithdrawal = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault(); // Prevent default link behavior
		// Your click handler logic here
		setShowWithdrawal(true);
		setShowWithdrawalLink(false);
	};

	const cancelWithdrawal = () => {
		setShowWithdrawal(false);
		setShowError(false);
		setWithdrawalReason(undefined);
		setShowWithdrawalLink(selectedReading?.status != ReadingStatus.WITHDRAWN);
	};

	const closeWindow = () => {
		setShowReadingModal(false);
	};

	// Life Cycle
	/**
	 * Closes the modal when the Escape key is pressed
	 */
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setShowPhotoModal(false);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Layout
	const PhotoComponent = () =>
		getMeterReadingImage.data ? (
			<div className={styles['image-container']}>
				<picture>
					<img
						src={'data:image/jpg;base64,' + getMeterReadingImage.data}
						className={styles['image']}
						alt={readingPhotoName}
						title={readingPhotoName}
					/>
				</picture>
				<ButtonExpandPhoto />
			</div>
		) : (
			<></>
		);

	const ButtonExpandPhoto = () => {
		return (
			<Button
				ariaLabel="expand"
				customStyles={styles['button-expand']}
				onClick={() => setShowPhotoModal(true)}
			>
				<Enlarge className={styles['button-expand-icon']} />
			</Button>
		);
	};

	const ImageModal = () => (
		<Modal
			customModalStyle={styles['image-modal']}
			open={showPhotoModal}
			closeable
			handleClose={() => setShowPhotoModal(false)}
		>
			<picture className={styles['picture-full']}>
				<img
					src={'data:image/jpg;base64,' + getMeterReadingImage.data}
					className={styles['image-full']}
					alt={readingPhotoName}
					title={readingPhotoName}
				/>
			</picture>
		</Modal>
	);

	const WithdrawalComponent = () => (
		<div className={styles.withdraw}>
			{showWithdrawal ? (
				<>
					<h3>Withdrawal this reading</h3>
					<div>Select withdrawal reason</div>
					<SelectField
						label=""
						options={[
							WithdrawalReasonType.WRONG_DEVICE_SELECTED,
							WithdrawalReasonType.WRONG_NUMBER_ENTERED,
							WithdrawalReasonType.WRONG_UNIT_OF_MEASURE_SELECTED,
							WithdrawalReasonType.OTHER,
						]}
						placeholder="Select your option"
						errorMsg="Please select an item"
						disabled={showSuccess}
						handleChange={v => {
							setWithdrawalReason(v as WithdrawalReasonType);
						}}
					/>
				</>
			) : (
				<></>
			)}
			{(showError || showSuccess) && (
				<div className={styles.status}>
					<Status
						status={showSuccess ? 'success' : 'error'}
						show={true}
						onClose={() => {
							setShowError(false);
							setShowSuccess(false);
						}}
						title={showSuccess ? 'Updated details have been successfully saved.' : undefined}
						msg={
							showSuccess
								? 'Update successfully saved.'
								: 'Submission failed due to server error. Please try again later.'
						}
					/>
				</div>
			)}
			{showWithdrawal && !showSuccess && (
				<div className={styles['button-submit-container']}>
					<Button
						customStyles={styles.button}
						onClick={cancelWithdrawal}
						size="small"
						type="secondary"
						isLoading={updateReading.isPending}
					>
						Cancel
					</Button>
					<Button
						customStyles={styles.button}
						onClick={submitWithdrawalForm}
						disabled={withdrawalReason == undefined}
						size="small"
						type="primary"
						autoFocus={true}
						aria-label="Verify"
						data-testid="verify"
						isLoading={updateReading.isPending}
					>
						Withdraw
					</Button>
				</div>
			)}
			{showSuccess && (
				<div className={styles['button-submit-container']}>
					<Button customStyles={styles.button} onClick={closeWindow} size="small" type="primary">
						Close
					</Button>
				</div>
			)}
		</div>
	);

	return selectedReading ? (
		!showPhotoModal ? (
			<div className={styles.content}>
				<div className={styles['text-content']}>
					<h3>
						{selectedMeter?.displayName ? selectedMeter.displayName : selectedMeter?.reference}
					</h3>
					<p>
						Reading Date:{' '}
						{selectedReading.readingDate
							? convertDateAndTimeToDisplayFormat(selectedReading.readingDate)
							: '-'}
					</p>
					<p>Status: {selectedReading.status ? selectedReading.status : 'Pending'}</p>
					<p>Reading: {displayReadingValue(selectedReading) ?? '-'}</p>
					{hasAssessedTake(selectedReading) ? (
						<p>
							Usage:{' '}
							<AssessedTake
								value={selectedReading?.assessedTake?.toString()}
								tooltipPosition="left"
							/>
						</p>
					) : (selectedReading.status === ReadingStatus.VERIFIED ||
							selectedReading.status === ReadingStatus.SUBMITTED) &&
					  selectedReading.take !== undefined ? (
						<p>Usage: {selectedReading.take.toFixed(3).concat(' ML')}</p>
					) : (
						<p>Usage: Pending</p>
					)}
					<p>
						Source:
						{' ' + displayResourceSourceType(selectedReading.sourceType)}
					</p>
					{showWithdrawalLink && (
						<p>
							<a onClick={handleWithdrawal}>Withdraw this reading</a>
						</p>
					)}
					{WithdrawalComponent()}
				</div>
				{getMeterReadingImage.isLoading || getMeterReadingImage.data ? (
					<RequestedDataRenderer
						fulfilledComponent={PhotoComponent()}
						emptyComponent={<></>}
						loaderComponent={
							<div className={styles['image-container--loading']}>
								<Loader />
							</div>
						}
						queryStatus={getMeterReadingImage.status}
						data={getMeterReadingImage.data}
						error={getMeterReadingImage.error}
					/>
				) : (
					<></>
				)}
			</div>
		) : (
			<ImageModal />
		)
	) : (
		<div></div>
	);
};
